/** Max edge for the re-encoded logo; mirrors Odoo's image_1920 storage. */
const MAX_LOGO_DIMENSION = 1920;

/**
 * Re-encode a user-picked image to a clean, single-frame PNG via a canvas.
 *
 * Phone cameras routinely save JPEGs inside the multi-frame "MPO" container
 * (Multi-Picture Object, used for HDR/depth). The browser still reports these
 * as `image/jpeg`, so they pass the client allowlist, but the backend's image
 * library detects the real format as `MPO` and rejects it. Drawing the decoded
 * image onto a canvas and exporting collapses it to a plain PNG the backend
 * always accepts, and strips EXIF/orientation noise in the process. Large
 * images are downscaled to {@link MAX_LOGO_DIMENSION} to keep the upload small.
 */
export function reencodeImageToPng(file: File): Promise<File> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();

		img.onload = () => {
			URL.revokeObjectURL(url);

			const scale = Math.min(1, MAX_LOGO_DIMENSION / Math.max(img.width, img.height));
			const canvas = document.createElement('canvas');
			canvas.width = Math.round(img.width * scale);
			canvas.height = Math.round(img.height * scale);

			const ctx = canvas.getContext('2d');
			if (!ctx) {
				return reject(new Error('Canvas context unavailable'));
			}

			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			canvas.toBlob((blob) => {
				if (!blob) {
					return reject(new Error('Image encoding failed'));
				}
				resolve(new File([blob], 'logo.png', { type: 'image/png' }));
			}, 'image/png');
		};

		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('Image could not be read'));
		};

		img.src = url;
	});
}
